import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    subscription_price: 0,
    currency: "NGN",
    paystack_public_key: "",
    paystack_secret_key: "",
  });
  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings({ ...settings, ...r.data }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Don't resend masked secret key
      const payload = {
        subscription_price: Number(settings.subscription_price),
        currency: settings.currency,
        paystack_public_key: settings.paystack_public_key,
      };
      if (settings.paystack_secret_key && !settings.paystack_secret_key.includes("****")) {
        payload.paystack_secret_key = settings.paystack_secret_key;
      }
      const { data } = await api.put("/settings", payload);
      setSettings({ ...settings, ...data });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="System Config" description="App configuration & payment provider." />
        <Card className="border-border/70 shadow-sm">
          <div className="p-6 space-y-4">
            <div className="h-4 w-32 shimmer rounded" />
            <div className="h-10 w-full shimmer rounded" />
            <div className="h-10 w-full shimmer rounded" />
            <div className="h-10 w-full shimmer rounded" />
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="System Config"
        description="Configure subscription pricing, currency and Paystack credentials."
      />

      <form onSubmit={onSave} className="space-y-5">
        <Card className="border-border/70 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Subscription</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-price">Price</Label>
              <Input
                id="s-price"
                type="number"
                min="0"
                step="0.01"
                value={settings.subscription_price}
                onChange={(e) => setSettings({ ...settings, subscription_price: e.target.value })}
                data-testid="settings-price-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-currency">Currency</Label>
              <Input
                id="s-currency"
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value.toUpperCase() })}
                data-testid="settings-currency-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/70 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Paystack</CardTitle>
            <p className="text-xs text-muted-foreground">
              The secret key is hidden after save. Paste a new one to update it.
            </p>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-pubkey">Public key</Label>
              <Input
                id="s-pubkey"
                value={settings.paystack_public_key || ""}
                onChange={(e) => setSettings({ ...settings, paystack_public_key: e.target.value })}
                placeholder="pk_live_…"
                data-testid="settings-pubkey-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-seckey">Secret key</Label>
              <Input
                id="s-seckey"
                type="password"
                value={settings.paystack_secret_key || ""}
                onChange={(e) => setSettings({ ...settings, paystack_secret_key: e.target.value })}
                placeholder="sk_live_…"
                data-testid="settings-seckey-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="size-4 mr-1.5 animate-spin" /> : <Save className="size-4 mr-1.5" />}
            Save changes
          </Button>
        </div>
      </form>
    </>
  );
}
